/**
 * #2133 設計 §3/§4 + デザイン: ロールごとに存在する画面だけをメニューに出す
 * (「準備中」と出るだけの画面・項目は作らない)。サイドバーとホームの
 * カードはこの1か所の定義から作る(出す画面の判定を2か所に書かない)。
 *
 * 表示の可否は App.tsx の RequireRole と同じロール条件にすること
 * (ここで出しても、ルート側で許可されていなければ開けない)。
 */
import type { IconName } from '@/components/ui/Icon';
import type { RoleFlags } from '@/lib/roleFlags';

export interface NavItem {
  to: string;
  labelKey: string;
  /** ホームのカードに出す一行説明 */
  descriptionKey: string;
  icon: IconName;
}

export interface NavGroup {
  /** グループ見出し(無ければ見出しなし) */
  labelKey: string | null;
  items: NavItem[];
}

const ITEMS = {
  home: { to: '/', labelKey: 'nav.home', descriptionKey: 'home.description', icon: 'home' },
  companies: { to: '/companies', labelKey: 'nav.companies', descriptionKey: 'home.actions.companies', icon: 'company' },
  contracts: {
    to: '/contracts',
    labelKey: 'nav.contracts',
    descriptionKey: 'home.actions.contracts',
    icon: 'contracts',
  },
  users: { to: '/users', labelKey: 'nav.users', descriptionKey: 'home.actions.users', icon: 'search' },
  studios: { to: '/studios', labelKey: 'nav.studios', descriptionKey: 'home.actions.studios', icon: 'studio' },
  students: { to: '/students', labelKey: 'nav.students', descriptionKey: 'home.actions.students', icon: 'students' },
  instructors: {
    to: '/instructors',
    labelKey: 'nav.instructors',
    descriptionKey: 'home.actions.instructors',
    icon: 'instructors',
  },
  adminAccounts: {
    to: '/admin-accounts',
    labelKey: 'nav.adminAccounts',
    descriptionKey: 'home.actions.adminAccounts',
    icon: 'staff',
  },
  lessons: { to: '/lessons', labelKey: 'nav.lessons', descriptionKey: 'home.actions.lessons', icon: 'lessons' },
  sequences: { to: '/sequences', labelKey: 'nav.sequences', descriptionKey: 'home.actions.sequences', icon: 'sequences' },
  poses: { to: '/poses', labelKey: 'nav.poses', descriptionKey: 'home.actions.poses', icon: 'poses' },
  poseCategories: {
    to: '/pose-categories',
    labelKey: 'nav.poseCategories',
    descriptionKey: 'home.actions.poseCategories',
    icon: 'categories',
  },
  mats: { to: '/mats', labelKey: 'nav.mats', descriptionKey: 'home.actions.mats', icon: 'mats' },
  setting: { to: '/setting', labelKey: 'nav.setting', descriptionKey: 'home.actions.setting', icon: 'settings' },
} satisfies Record<string, NavItem>;

export const HOME_ITEM: NavItem = ITEMS.home;
export const SETTING_ITEM: NavItem = ITEMS.setting;

/**
 * ホーム・設定を除く、ロールごとのメニュー(グループ分け済み)。
 *
 * hasActiveCompany: admin が会社一覧(ActiveCompanyContext)で会社を選んで
 * いるか。admin は会社を選ぶまでは「会社」(切り替え用)のメニューだけを
 * 出す。選んだ後は companyAssociate/studioAssociate を合わせた全ての画面
 * (生徒・講師・担当者アカウント・レッスン・シーケンス・ポーズ・スタジオ・
 * マット)を出す(#2133 社長指示2026-10-13: 「同じ画面を使える」ため、
 * studioAssociate 専用のポーズも admin には出す)。
 */
export function buildNavGroups(roleFlags: RoleFlags, hasActiveCompany: boolean): NavGroup[] {
  const groups: NavGroup[] = [];

  if (roleFlags.isAdmin) {
    // #2133 第2弾: ユーザー検索(設計 §3)と契約情報(§4)は会社をまたぐので、
    // 会社を選ぶ前から「会社」の次に出す。
    groups.push({ labelKey: 'nav.groupOperations', items: [ITEMS.companies, ITEMS.users, ITEMS.contracts] });
    if (hasActiveCompany) {
      groups.push({
        labelKey: 'nav.groupMembers',
        items: [ITEMS.students, ITEMS.instructors, ITEMS.adminAccounts],
      });
      groups.push({ labelKey: 'nav.groupLessons', items: [ITEMS.lessons, ITEMS.sequences, ITEMS.poses, ITEMS.poseCategories] });
      groups.push({ labelKey: 'nav.groupFacilities', items: [ITEMS.studios, ITEMS.mats] });
    }
    return groups;
  }

  if (roleFlags.isCompanyAssociate || roleFlags.isStudioAssociate) {
    groups.push({
      labelKey: 'nav.groupMembers',
      items: [ITEMS.students, ITEMS.instructors, ITEMS.adminAccounts],
    });
    groups.push({
      labelKey: 'nav.groupLessons',
      items: [ITEMS.lessons, ITEMS.sequences, ITEMS.poses, ITEMS.poseCategories],
    });
    groups.push({
      labelKey: 'nav.groupFacilities',
      items: roleFlags.isCompanyAssociate ? [ITEMS.studios, ITEMS.mats] : [ITEMS.mats],
    });
  }

  return groups;
}

/** 画面上部・ブラウザのタブに出すロールごとの名前 */
export function roleTitleKey(roleFlags: RoleFlags): string {
  if (roleFlags.isAdmin) return 'appTitle.admin';
  if (roleFlags.isCompanyAssociate) return 'appTitle.companyAssociate';
  if (roleFlags.isStudioAssociate) return 'appTitle.studioAssociate';
  return 'common.appName';
}
